import instance from "api/globalaxios";

const cleanParams = (params) => {
  const out = {};
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (
      value !== undefined &&
      value !== null &&
      value !== "" &&
      value !== "All" &&
      value !== "All Cases"
    ) {
      out[key] = value;
    }
  });
  return out;
};

// ---- Report 1: Arbitrator ----------------------------------------------------

export const getBacArbitratorReport = (startDate, endDate, page, search) =>
  instance.get(`/bac/reports/arbitrator/`, {
    params: cleanParams({ start_date: startDate, end_date: endDate, page, search }),
  });

export const printBacArbitratorReport = (startDate, endDate, search) =>
  instance.get(`/bac/reports/arbitrator/generate/`, {
    params: cleanParams({ start_date: startDate, end_date: endDate, search }),
    responseType: "blob",
  });

// ---- Report 2: Pending Cases -------------------------------------------------

export const getBacPendingCases = (category, caseType, startDate, endDate, stageId, page, search) =>
  instance.get(`/bac/reports/pending-cases/`, {
    params: cleanParams({
      category,
      case_type: caseType,
      start_date: startDate,
      end_date: endDate,
      stage_id: stageId,
      page,
      search,
    }),
  });

export const printBacPendingReport = (category, caseType, startDate, endDate, stageId, search) =>
  instance.get(`/bac/reports/pending-cases/generate/`, {
    params: cleanParams({
      category,
      case_type: caseType,
      start_date: startDate,
      end_date: endDate,
      stage_id: stageId,
      search,
    }),
    responseType: "blob",
  });

// ---- Report 6: Undated Cases -------------------------------------------------
// Open cases with no hearing booked from today onwards. bookingStatus is
// "never" (never booked) or "lapsed" (last hearing passed); omit for both.

export const getBacUndatedCases = (category, caseType, startDate, endDate, stageId, bookingStatus, page, search) =>
  instance.get(`/bac/reports/undated-cases/`, {
    params: cleanParams({
      category,
      case_type: caseType,
      start_date: startDate,
      end_date: endDate,
      stage_id: stageId,
      booking_status: bookingStatus,
      page,
      search,
    }),
  });

export const printBacUndatedReport = (category, caseType, startDate, endDate, stageId, bookingStatus, search) =>
  instance.get(`/bac/reports/undated-cases/generate/`, {
    params: cleanParams({
      category,
      case_type: caseType,
      start_date: startDate,
      end_date: endDate,
      stage_id: stageId,
      booking_status: bookingStatus,
      search,
    }),
    responseType: "blob",
  });

// ---- Report 3: Disposal ------------------------------------------------------

export const getBacDisposal = (category, caseType, startDate, endDate, page, search) =>
  instance.get(`/bac/reports/disposal/`, {
    params: cleanParams({
      category,
      case_type: caseType,
      start_date: startDate,
      end_date: endDate,
      page,
      search,
    }),
  });

export const printBacDisposalReport = (category, caseType, startDate, endDate, search) =>
  instance.get(`/bac/reports/disposal/generate/`, {
    params: cleanParams({
      category,
      case_type: caseType,
      start_date: startDate,
      end_date: endDate,
      search,
    }),
    responseType: "blob",
  });

// ---- Report 4: Cause List ----------------------------------------------------

export const getBacCauseListReport = (startDate, endDate, page, search, cause_list_type) =>
  instance.get(`/bac/reports/causelist/`, {
    params: cleanParams({ start_date: startDate, end_date: endDate, page, search, cause_list_type }),
  });

export const printBacCauseListReport = (startDate, endDate, search, cause_list_type) =>
  instance.get(`/bac/reports/causelist/generate/`, {
    params: cleanParams({ start_date: startDate, end_date: endDate, search, cause_list_type }),
    responseType: "blob",
  });

// ---- Report 5: Final Award ---------------------------------------------------

export const getBacFinalAwardReport = (category, dateFrom, dateTo, search, page) =>
  instance.get(`/bac/reports/final-award/`, {
    params: cleanParams({ category, date_from: dateFrom, date_to: dateTo, search, page }),
  });

export const printBacFinalAwardReport = (category, dateFrom, dateTo, search) =>
  instance.get(`/bac/reports/final-award/download/`, {
    params: cleanParams({ category, date_from: dateFrom, date_to: dateTo, search }),
    responseType: "blob",
  });

// ---- Report 7: Slot Availability ---------------------------------------------
// Every hearing-room slot on one date as available / booked / past / holiday,
// computed live on each request. date is YYYY-MM-DD; roomIds is "3,5"; status
// is one of SLOT_STATUS_OPTIONS (omit both for every room and status).

export const getBacSlotAvailability = (date, roomIds, status) =>
  instance.get(`/bac/reports/slot-availability/`, {
    params: cleanParams({ date, room_id: roomIds, status }),
  });

export const printBacSlotAvailability = (date, roomIds, status) =>
  instance.get(`/bac/reports/slot-availability/generate/`, {
    params: cleanParams({ date, room_id: roomIds, status }),
    responseType: "blob",
  });

// The API's error message, whether the response body came back as JSON or (for
// a PDF download) as a Blob.
export const readApiError = async (error) => {
  let data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      data = JSON.parse(await data.text());
    } catch (e) {
      data = null;
    }
  }
  return data?.error || data?.detail || "";
};

// ---- Shared filter option lists ----------------------------------------------

export const BAC_CATEGORY_OPTIONS = [
  { label: "All", value: "All" },
  { label: "CMP Case", value: "cmp" },
  { label: "Reference Case", value: "reference" },
  { label: "Institutional Case", value: "institutional" },
  { label: "Other", value: "other" },
];

// Type filter used by Disposal, Pending & Undated reports.
export const BAC_TYPE_OPTIONS = ["All", "AC", "IAC"];

// Hearing Status filter of the Undated Cases report.
export const BAC_BOOKING_STATUS_OPTIONS = [
  { label: "All", value: "All" },
  { label: "Never booked", value: "never" },
  { label: "Last hearing passed", value: "lapsed" },
];

// Status filter of the Slot Availability report.
export const SLOT_STATUS_OPTIONS = [
  { label: "All", value: "All" },
  { label: "Available", value: "available" },
  { label: "Booked", value: "booked" },
  { label: "Past", value: "past" },
  { label: "Holiday", value: "holiday" },
];
